import { ValidationErrorMessages } from '@constants/forms';
import { validateEmail, validatePhoneNumber } from '@utils/validators';

import { SaveFormValues, ValidateSaveFormResult } from '../types';

const CONTACT_FIELD_REQUIRED_ERROR = 'At least one contact field is required';

interface SaveFormErrors {
  emails: (string | undefined)[];
  mobiles: (string | undefined)[];
}

const validateEmailInput = (email: string) => {
  if (!email) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  } else if (!validateEmail(email)) {
    return ValidationErrorMessages.INVALID_EMAIL;
  } else {
    return undefined;
  }
};

const validatePhoneInput = (phoneNumber: string) => {
  if (!phoneNumber) {
    return ValidationErrorMessages.FIELD_REQUIRED;
  } else if (!validatePhoneNumber(phoneNumber)) {
    return ValidationErrorMessages.INVALID_PHONE;
  } else {
    return undefined;
  }
};

const hasNoErrors = (errors: (string | undefined)[]) =>
  errors.every((error) => !error);

const hasPrimaryContact = (
  value: string,
  index: number,
  alternativeValue: string
) => !value && index === 0 && alternativeValue;

const validateSaveForm = (values: SaveFormValues): ValidateSaveFormResult => {
  const { emails, mobiles } = values;
  const errors: SaveFormErrors = {
    emails: [],
    mobiles: [],
  };

  const primaryEmail = emails[0];
  const primaryMobile = mobiles[0];
  const missingPrimaryContact = !primaryEmail && !primaryMobile;

  // NOTE: Adding another contact field is disabled when form is invalid
  if (missingPrimaryContact) {
    errors.emails = [CONTACT_FIELD_REQUIRED_ERROR];
    errors.mobiles = [CONTACT_FIELD_REQUIRED_ERROR];

    return errors;
  }

  emails.forEach((email, index) => {
    // If at least one primary contact is provided, the other is not required
    if (hasPrimaryContact(email, index, primaryMobile)) {
      errors.emails.push(undefined);
      return;
    }

    errors.emails[index] = validateEmailInput(email);
  });

  mobiles.forEach((mobile, index) => {
    // If at least one primary contact is provided, the other is not required
    if (hasPrimaryContact(mobile, index, primaryEmail)) {
      errors.mobiles.push(undefined);
      return;
    }

    errors.mobiles[index] = validatePhoneInput(mobile);
  });

  if (hasNoErrors(errors.mobiles) && hasNoErrors(errors.emails)) {
    return {};
  }

  return errors;
};

export { validateSaveForm, CONTACT_FIELD_REQUIRED_ERROR, hasNoErrors };
